import { motion } from "framer-motion";
import { Clapperboard, Camera, Scissors, PenLine, Drama } from "lucide-react";

const INSTRUCTORS = [
  {
    icon: Clapperboard,
    initials: "AS",
    subject: "DIRECTION & PRODUCTION",
    name: "Amit Singh",
    role: "Founder — Life Inside Media. Directs and produces client work for national and international brands.",
  },
  {
    icon: Camera,
    initials: "DP",
    subject: "CINEMATOGRAPHY",
    name: "Senior Director of Photography",
    role: "Life Inside Media — shoots LIM's commercial and branded productions on cinema-line cameras.",
  },
  {
    icon: Scissors,
    initials: "LE",
    subject: "EDITING & POST-PRODUCTION",
    name: "Lead Editor",
    role: "Life Inside Media — runs the post pipeline: edit, motion, color and delivery for live clients.",
  },
  {
    icon: PenLine,
    initials: "SW",
    subject: "SCREENPLAY WRITING",
    name: "Senior Writer",
    role: "Life Inside Media — writes the scripts and branded content that go into LIM productions.",
  },
  {
    icon: Drama,
    initials: "AC",
    subject: "ACTING FOR CAMERA",
    name: "Visiting Faculty",
    role: "Working actor from LIM productions — trains performance specifically for the lens.",
  },
];

function Portrait({ ins, index }) {
  return (
    <div className="relative aspect-[4/3] bg-lima-navy overflow-hidden" data-testid={`instructor-photo-${index}`}>
      {ins.photo ? (
        <img src={ins.photo} alt={ins.name} className="w-full h-full object-cover saturate-[.75]" loading="lazy" />
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <span className="font-serifd text-7xl md:text-8xl text-lima-blue/80 leading-none select-none" aria-hidden="true">
            {ins.initials}
          </span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      <span className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-lima-blue/80" aria-hidden="true" />
      <span className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-lima-blue/80" aria-hidden="true" />
      <span className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-lima-blue/80" aria-hidden="true" />
      <span className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-lima-blue/80" aria-hidden="true" />
      <span className="absolute bottom-3 left-1/2 -translate-x-1/2 font-mono2 text-[10px] tracking-[0.25em] text-white/80 bg-black/50 backdrop-blur px-2.5 py-1 border border-white/10">
        {ins.subject}
      </span>
    </div>
  );
}

export default function InstructorsSection() {
  return (
    <section className="bg-lima-cream text-lima-ink px-5 md:px-10 py-14 md:py-32 border-t border-black/10" data-testid="instructors-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-20">
        <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98]">
          Meet your <span className="font-serifit font-normal text-[#3a3a36]">instructors.</span>
        </h2>
        <p className="max-w-sm text-sm md:text-base text-black/55 leading-relaxed">
          Every subject is taught by the person who does it for clients all week — not a full-time lecturer.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {INSTRUCTORS.map((ins, i) => (
          <motion.article
            key={ins.subject}
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="bg-lima-ink group hover:-translate-y-1 transition-transform duration-300"
            data-testid={`instructor-${i}`}
          >
            <Portrait ins={ins} index={i} />
            <div className="p-7 md:p-8">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono2 text-[11px] tracking-[0.25em] text-[#8A8FFF]">{ins.subject}</p>
                <ins.icon size={16} className="text-white/35" />
              </div>
              <h3 className="font-display font-bold text-2xl tracking-tight text-lima-paper">{ins.name}</h3>
              <p className="mt-2.5 text-sm text-white/55 leading-relaxed">{ins.role}</p>
            </div>
          </motion.article>
        ))}
        <motion.div
          initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="bg-lima-ink p-8 md:p-10 flex flex-col justify-center border border-white/10"
        >
          <p className="font-serifit text-2xl md:text-3xl text-[#8A8FFF] leading-snug">+ guest sessions</p>
          <p className="mt-3 text-sm text-white/55 leading-relaxed">
            Working directors, editors and creators drop in through the program — people currently killing it in the
            industry.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
